import { Link } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import avatar from "assets/img/avatar.png";
import { ReactComponent as ThreeDots } from "assets/img/icons/three-dots.svg";
import { Friend } from "@compass/shared/types";
import { NoFriends } from "./NoFriends";
import { UnfriendModal } from "./UnfriendModal";

type MyFriendsProps = {
    friends?: Friend[];
    onRemoveFriend: ({ id }: { id: number }) => void;
    setModalOpen: (isOpen: boolean) => void;
};

export const MyFriends = ({ friends, onRemoveFriend, setModalOpen }: MyFriendsProps) => {
    const [showUnfriendModal, setShowUnfriendModal] = useState(false);
    const [selectedFriend, setSelectedFriend] = useState<Friend | null>(null);
    const [openDropdownId, setOpenDropdownId] = useState<number | null>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setOpenDropdownId(null);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    return (
        <>
            {!friends || friends.length === 0 ? (
                <NoFriends setModalOpen={setModalOpen} />
            ) : (
                <div className="flex h-4/6 items-start justify-center">
                    <div className="grid w-full grid-cols-3 gap-4">
                        {friends.map((friend) => (
                            <div
                                key={friend.id}
                                className="h-30 bg-brand-polar relative rounded-2xl border border-[#c8c8c8] p-4"
                                ref={openDropdownId === friend.id ? dropdownRef : null}
                            >
                                <div className="flex w-full items-start justify-between gap-4">
                                    <div className="flex items-start gap-4">
                                        <Link
                                            to="/profile/$id"
                                            target="_blank"
                                            params={{ id: `${friend.id}` }}
                                            className="text-inherit no-underline focus:outline-none focus:ring-0"
                                        >
                                            <img
                                                src={friend.avatarUrl || avatar}
                                                alt={friend.name}
                                                className="h-12 w-12 rounded-md bg-[#def1fa]"
                                            />
                                        </Link>
                                        <div className="flex flex-col">
                                            <Link
                                                to="/profile/$id"
                                                target="_blank"
                                                params={{ id: `${friend.id}` }}
                                                className="text-inherit no-underline focus:outline-none focus:ring-0"
                                            >
                                                <div className=" cursor-pointer font-semibold">
                                                    {friend.name}
                                                </div>
                                            </Link>
                                            {/* <div className="text-sm font-semibold">
                                                2 Mutual friends
                                            </div> */}
                                        </div>
                                    </div>
                                    <div className="relative">
                                        <ThreeDots
                                            className="cursor-pointer"
                                            onClick={() =>
                                                setOpenDropdownId(
                                                    openDropdownId === friend.id ? null : friend.id,
                                                )
                                            }
                                        />
                                        {openDropdownId === friend.id && (
                                            <div className="absolute right-0 z-10 mt-0 w-28 rounded-md border border-gray-200 bg-[#f7f7f7] shadow-sm">
                                                <button
                                                    onClick={() => {
                                                        setSelectedFriend(friend);
                                                        setShowUnfriendModal(true);
                                                        setOpenDropdownId(null);
                                                    }}
                                                    className="block w-full px-4 py-1 text-left text-sm text-gray-700 hover:bg-gray-100"
                                                >
                                                    Unfriend
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
            {selectedFriend && showUnfriendModal && (
                <UnfriendModal
                    isOpen={showUnfriendModal}
                    setIsOpen={setShowUnfriendModal}
                    userName={selectedFriend.firstName}
                    userId={selectedFriend.id}
                    onRemoveFriend={onRemoveFriend}
                />
            )}
        </>
    );
};
